/**
 * OB-01 Pipeline artefacts (INV-CH-OB-01, ENGINE-SPEC §4; changes C-D4-OB01, C-D7-OB01,
 * C-OB01-AICITE, C-OB01-NOSHRINK).
 *
 * Unshrunk (`shrink: false`, operator decision 2026-09-29): the estimate is the raw weighted rate, not pulled
 * toward the human prior. A residue hit is near-conclusive, so one "As an AI language model" in 1,000 words
 * reads at the ramp value of its raw rate (≈ 52), not ≈ 39. κ still reports the data weight.
 *
 * Weighted residue events per 1,000 words (the seed's raw count `n`, never normalised, D7, is now a
 * rate with the seed ramp re-expressed at a 1,000-word reference):
 *   (a) markup residue ×3 (`**x**`, `__x__`, `##` heads, code fences), counted ONLY when
 *       doc.observability.markdownResidueObservable is true (txt, pdf, paste without Markdown
 *       detection, D4): in a parsed Markdown/HTML/DOCX document the syntax is structure, not residue;
 *   (b) orphaned `[n]` citation brackets ×1;
 *   (c) chat-interface leftovers ("as an AI language model", …) ×4;
 *   (d) the v3 aiCite lexicon (oaicite, contentReference[, 【n†, turnNsearchM, utm_source=chatgpt.com) ×4.
 * (e) Prompt residue in the first 300 characters (two or more instruction phrases, the user's own
 *     prompt pasted with the output) is a positional BINARY feature: not an event, it lifts the
 *     display to 90. Display = max(count-ramp score, 90 when (e) is present).
 *
 * Registers: OB-01 reads every register (ENGINE-SPEC §2.2); events and exposure are therefore over
 * all words (prose, structural, quoted, code). On an unsupported language the English lexicon
 * parts ((c) and (e)) abstain; markup, brackets and aiCite still run (the channel is agnostic).
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons, safeScan } from '../lexicons/index.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt } from './helpers.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Measurement,
	Ramp,
	RunContext,
} from './types.ts';

/** The seed's count ramp at a 1,000-word reference, with the D2 zero guard 10 (already the first point). */
const RAMP: Ramp = [
	[0, 10],
	[1, 52],
	[3, 78],
	[6, 90],
	[12, 96],
];

const F_MARKUP = 1;
const F_CITE = 2;
const F_CHAT = 3;
const F_AICITE = 4;
const F_PROMPT = 5;
const W_MARKUP = 3;
const W_CITE = 1;
const W_CHAT = 4;
const W_AICITE = 4;
export const PROMPT_SCORE = 90;
const HEAD = 300;
const PROMPT_MIN_HITS = 2;

export const ob01Def: ChannelDef = {
	id: 'OB-01',
	name: 'Pipeline artefacts',
	family: 'RESIDUE',
	axis: 'authorship',
	registers: ['prose', 'structural', 'quoted', 'code'],
	direction: 1,
	oneWay: true,
	estimator: 'rate',
	features: [
		{ id: 'residue', label: 'Weighted residue events per 1,000 words' },
		{ id: 'markup', label: 'Markdown markup residue (×3; txt/pdf/paste only)' },
		{ id: 'citeBracket', label: 'Orphaned [n] citation bracket' },
		{ id: 'chatLeftover', label: 'Chat-interface leftover (×4)' },
		{ id: 'aiCite', label: 'Chat citation / tracking residue (×4)' },
		{ id: 'promptResidue', label: 'Prompt residue in the first 300 characters (binary)' },
	],
	minExposure: 150,
	exposureUnit: 'words',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1000,
	unitLabel: 'per 1k words (weighted)',
	prior: derivePrior(RAMP, 1000, 'words', 1.5),
	// residue is near-conclusive by design: one hit in a short text is not pulled toward the human prior (C-OB01-NOSHRINK)
	shrink: false,
	weight: 1.5,
	nd: 0.1,
	changes: ['C-D4-OB01', 'C-D7-OB01', 'C-OB01-AICITE', 'C-OB01-NOSHRINK'],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

const clip = (s: string, n: number): string => s.replace(/\n/g, ' ').slice(0, n);

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const L = lex();
	const nr = L.channels['ch-ob01'];
	const markupOn = doc.observability.markdownResidueObservable;
	const langOn = !doc.flags.languageUnsupported;
	const reMarkup = nr.get('markup');
	const reCite = nr.get('citeBracket');
	const reChat = nr.get('chatLeftover');
	const iMarkup = nr.index.markup as number;
	const iCite = nr.index.citeBracket as number;
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	for (let p = 0; p < P; p++) e[p] = doc.paragraphs.words[p] as number;
	const ev = makeEvidence();
	const markups: string[] = [];
	const chats: string[] = [];
	let cites = 0;
	let aicites = 0;

	for (const b of doc.blocks) {
		if (b.para < 0 || b.charEnd <= b.charStart || b.sceneBreak) continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		const p = b.para;
		let kp = 0;
		const isCode = b.register === 'code';
		if (markupOn && nr.list.admits(iMarkup, text, text)) {
			safeScan(reMarkup, text, b.charStart, (s, en, m) => {
				// the `##` alternative consumes the newline before it: the evidence is the head itself
				const lead = m[0].startsWith('\n') ? 1 : 0;
				if (isCode && !m[0].startsWith('```')) return;
				ev.add(s + lead, en, F_MARKUP, W_MARKUP);
				kp += W_MARKUP;
				markups.push(clip(m[0].slice(lead), 50));
			});
		}
		if (isCode) {
			k[p] = (k[p] as number) + kp;
			continue;
		}
		if (nr.list.admits(iCite, text, text)) {
			safeScan(reCite, text, b.charStart, (s, en) => {
				ev.add(s, en, F_CITE, W_CITE);
				kp += W_CITE;
				cites++;
			});
		}
		if (langOn) {
			safeScan(reChat, text, b.charStart, (s, en, m) => {
				ev.add(s, en, F_CHAT, W_CHAT);
				kp += W_CHAT;
				chats.push(m[0]);
			});
		}
		L.ob01Aicite.scanBlock(text, b.charStart, (s, en) => {
			ev.add(s, en, F_AICITE, W_AICITE);
			kp += W_AICITE;
			aicites++;
		});
		k[p] = (k[p] as number) + kp;
	}

	// (e) positional binary feature: prompt residue in the first 300 characters
	let promptHits = 0;
	if (langOn) {
		const head = doc.text.slice(0, HEAD);
		const rePrompt = nr.get('promptResidue');
		const found: Array<[number, number]> = [];
		safeScan(rePrompt, head, 0, (s, en) => {
			found.push([s, en]);
			promptHits++;
		});
		if (promptHits >= PROMPT_MIN_HITS) for (const [s, en] of found) ev.add(s, en, F_PROMPT, 0);
		else promptHits = 0;
	}

	const summary: string[] = [];
	for (const x of markups.slice(0, 5)) summary.push(`“${x}”: markup residue in plain text`);
	if (cites > 0)
		summary.push(
			`${cites}× orphaned citation bracket: [n] with no reference apparatus, weak alone`
		);
	for (const x of chats.slice(0, 3)) summary.push(`“${x}”: chat-interface leftover`);
	if (aicites > 0)
		summary.push(`${aicites}× chat citation / tracking residue: oaicite, 【n†, turnNsearchM …`);
	if (promptHits > 0)
		summary.push(
			`${promptHits} instruction phrases in opening lines: conversation-paste residue, the prompt came along with the output`
		);
	const notes: string[] = [];
	if (!markupOn)
		notes.push(
			'markup residue not counted: Markdown syntax is structure, not an artefact, in this format (D4)'
		);
	if (!langOn) notes.push('chat and prompt lexicons not applied (unsupported language)');
	const words = doc.stats.words;
	const weighted =
		markups.length * W_MARKUP + cites * W_CITE + chats.length * W_CHAT + aicites * W_AICITE;
	summary.push(
		`${markups.length} markup · ${cites} cite-bracket · ${chats.length} chat · ${aicites} aiCite · ${promptHits > 0 ? 1 : 0} prompt-residue (${fmt(words > 0 ? (weighted / words) * 1000 : 0, 2)}/1000w weighted)`
	);
	return {
		k,
		e,
		evidence: ev,
		notes,
		features: {
			markup: markups.length,
			citeBrackets: cites,
			chatLeftovers: chats.length,
			aiCite: aicites,
			promptResidue: promptHits > 0 ? 1 : 0,
			markupObservable: markupOn ? 1 : 0,
		},
		summary,
	};
}

const base: ChannelImpl = rateChannel(ob01Def, measure);

/** Display = max(count-ramp score, 90 when prompt residue is present in the opening). */
export const ob01: ChannelImpl = {
	def: base.def,
	phase: base.phase,
	measure: base.measure,
	run(ctx: RunContext): ChannelResult {
		const r = base.run(ctx);
		if (r.status === 'scored' && r.features.promptResidue === 1) {
			r.doc.score = Math.max(r.doc.score, PROMPT_SCORE);
			r.doc.scoreCi90 = [
				Math.max(r.doc.scoreCi90[0], PROMPT_SCORE),
				Math.max(r.doc.scoreCi90[1], PROMPT_SCORE),
			];
		}
		return r;
	},
};
export { ob01Def as def, ob01 as impl };
